import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  Monitor, Copy, Mail, ArrowRight, Terminal, Check, ChevronLeft,
} from "lucide-react";
import { api } from "@/lib/api";

/**
 * <MobileGate />
 *
 * Friendly "Open on Desktop" screen for routes that don't fit comfortably on
 * a phone (Hedge Ledger, Commodity Desk, Cash-Flow Forecast, Admin pages…).
 *
 * Behaviour:
 *  - On viewports < 768px (md breakpoint) → renders this gate.
 *  - On md+ → renders the wrapped page unchanged.
 *  - Users can copy the deep-link, email it to themselves, or
 *    force-continue ("Continue anyway") if they really want to use the
 *    desktop UI on mobile.
 *
 * Usage:
 *   <MobileGate label="Hedge Ledger">
 *     <HedgeLedger />
 *   </MobileGate>
 */
export default function MobileGate({ children, label = "This view" }) {
  const loc = useLocation();
  const [forceShow, setForceShow] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);

  const url =
    typeof window !== "undefined"
      ? window.location.origin + loc.pathname + loc.search
      : "";

  // Tailwind's `md` breakpoint kicks in at 768px. We render the children
  // inside a wrapper that hides the gate on md+ via classes — no JS resize
  // listeners needed, so SSR-safe and no layout flicker.
  if (forceShow) return children;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — long-press the URL bar to copy manually");
    }
  };

  const emailLink = async (e) => {
    e?.preventDefault?.();
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setEmailing(true);
    try {
      await api.post("/public/share-link", {
        email,
        url,
        label,
      });
      setEmailSent(true);
      toast.success("Sent — check your inbox on a laptop or desktop");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send. Try copying the link instead.");
    } finally {
      setEmailing(false);
    }
  };

  return (
    <>
      {/* On desktop / tablet (md+) we just render the child page. */}
      <div className="hidden md:block">{children}</div>

      {/* On mobile (< md) we show the friendly gate. */}
      <div
        className="md:hidden min-h-[calc(100vh-56px)] bg-black text-white"
        data-testid="mobile-gate"
      >
        <div className="px-5 py-8">
          {/* Tag */}
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFB000] mb-3">
            // BLOOMBERG-STYLE TERMINAL
          </div>

          {/* Headline */}
          <h1 className="font-heading font-black text-3xl leading-[1.05] tracking-tight">
            {label} is built for a bigger screen.
          </h1>
          <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
            Hedge ledgers, monetization maps, and admin pipelines need real
            estate that phones can't give you. Here are three ways to keep going:
          </p>

          {/* Big visual */}
          <div className="mt-6 border border-zinc-900 bg-zinc-950/50 p-5 flex items-center gap-4">
            <span className="border border-[#FFB000]/40 bg-[#FFB000]/[0.08] p-3 flex-shrink-0">
              <Monitor className="w-6 h-6 text-[#FFB000]" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                THIS PAGE
              </div>
              <div className="font-mono text-xs text-zinc-300 truncate mt-0.5">
                {url}
              </div>
            </div>
          </div>

          {/* Action 1 — copy */}
          <button
            type="button"
            onClick={copyLink}
            data-testid="mobile-gate-copy-btn"
            className="mt-4 w-full border border-zinc-800 hover:border-zinc-700 bg-zinc-950/40 px-4 py-4 flex items-center justify-between gap-3 transition-colors"
          >
            <span className="flex items-center gap-3">
              {copied ? (
                <Check className="w-4 h-4 text-[#00E676]" />
              ) : (
                <Copy className="w-4 h-4 text-[#FFB000]" />
              )}
              <span className="font-mono text-[11px] uppercase tracking-widest text-white">
                {copied ? "COPIED" : "COPY LINK"}
              </span>
            </span>
            <ArrowRight className="w-4 h-4 text-zinc-600" />
          </button>

          {/* Action 2 — email */}
          <div className="mt-3 border border-zinc-800 bg-zinc-950/40 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Mail className="w-4 h-4 text-[#FFB000]" />
              <span className="font-mono text-[11px] uppercase tracking-widest text-white">
                EMAIL ME THE LINK
              </span>
            </div>
            {!emailSent && (
              <form onSubmit={emailLink} className="space-y-2">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  data-testid="mobile-gate-email-input"
                  className="w-full bg-black border border-zinc-800 px-3 py-3 font-mono text-sm text-white focus:outline-none focus:border-[#FFB000]"
                />
                <button
                  type="submit"
                  disabled={emailing}
                  data-testid="mobile-gate-email-btn"
                  className="w-full bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-4 py-3 hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                >
                  {emailing ? "SENDING…" : "SEND TO MY INBOX"}
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </form>
            )}
            {emailSent && (
              <div className="flex items-center gap-2 text-sm text-[#00E676]">
                <Check className="w-4 h-4" />
                <span>Email sent to {email}.</span>
              </div>
            )}
          </div>

          {/* Action 3 — escape hatch */}
          <button
            type="button"
            onClick={() => setForceShow(true)}
            data-testid="mobile-gate-force-btn"
            className="mt-6 w-full text-center font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-white py-2 transition-colors"
          >
            CONTINUE ANYWAY · I KNOW IT WILL BE CRAMPED →
          </button>

          {/* Suggested mobile destinations */}
          <div className="mt-10 border-t border-zinc-900 pt-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600 mb-3">
              // BUILT FOR YOUR PHONE
            </div>
            <div className="space-y-2">
              <MobileLink to="/app/today" icon={Terminal} label="Today" hint="Daily DARA briefing & top numbers" />
              <MobileLink to="/app/pos" icon={Terminal} label="POS Live" hint="Real-time transactions for shop floors" />
              <MobileLink to="/app/sources" icon={Terminal} label="Data Sources" hint="Connect Xero · MYOB · Square" />
              <MobileLink to="/app/settings" icon={Terminal} label="Settings" hint="Profile · Privacy · 2FA" />
            </div>
          </div>

          {/* Back */}
          <div className="mt-8">
            <Link
              to="/app"
              className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-white inline-flex items-center gap-1"
            >
              <ChevronLeft className="w-3 h-3" /> BACK TO OVERVIEW
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}

function MobileLink({ to, icon: Icon, label, hint }) {
  return (
    <Link
      to={to}
      data-testid={`mobile-gate-suggest-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="flex items-center gap-3 border border-zinc-900 hover:border-[#FFB000]/40 bg-zinc-950/30 px-4 py-3 transition-colors"
    >
      <Icon className="w-4 h-4 text-[#FFB000]" />
      <div className="flex-1 min-w-0">
        <div className="font-mono text-[11px] uppercase tracking-widest text-white">
          {label}
        </div>
        <div className="font-mono text-[10px] text-zinc-500 truncate">{hint}</div>
      </div>
      <ArrowRight className="w-3.5 h-3.5 text-zinc-600" />
    </Link>
  );
}
